/**
 * Web Serial API Communication Module for GPIO Extender
 * Protocol: CDC Serial at 115200 baud
 * Command format: ~<CMD><PARAMS>\r\n
 */

import { EventEmitter } from '../utils/events.js';
import {
    PIN_GPIO_MAP,
    PIN_MODES,
    USB_PRODUCT_ID,
    USB_VENDOR_ID,
    connectorPinToProtocol,
    isSuccessfulResponse,
    splitScript,
    validateCdcCommand,
    validateModes,
    validatePwmDuty,
    validatePwmFrequency,
    validateScript,
    validateWS2812Hex
} from './protocol.js';

export { PIN_GPIO_MAP, PIN_MODES } from './protocol.js';

export class SerialConnection extends EventEmitter {
    constructor() {
        super();
        this._port = null;
        this._reader = null;
        this._writer = null;
        this._readBuffer = '';
        this._connected = false;
        this._readLoopActive = false;
        this._commandQueue = Promise.resolve();
        this._pendingResponse = null;
        this._deviceInfo = '';
        this._disconnecting = false;
        this._disconnectPromise = null;

        // Listen for device disconnection
        this._onPortDisconnect = this._onPortDisconnect.bind(this);
        if (SerialConnection.isSupported()) {
            navigator.serial.addEventListener('disconnect', this._onPortDisconnect);
        }
    }

    /**
     * Handle port disconnection event from Web Serial API
     */
    _onPortDisconnect(event) {
        const disconnectedPort = event.port || event.target;
        if (this._port && disconnectedPort === this._port) {
            void this.disconnect();
        }
    }

    get connected() {
        return this._connected;
    }

    get deviceInfo() {
        return this._deviceInfo;
    }

    /**
     * Check if Web Serial API is supported
     */
    static isSupported() {
        return typeof navigator !== 'undefined' && 'serial' in navigator;
    }

    /**
     * Request a serial port from the user
     */
    async requestPort() {
        if (!SerialConnection.isSupported()) {
            throw new Error('Web Serial API not supported');
        }

        try {
            this._port = await navigator.serial.requestPort({
                filters: [
                    { usbVendorId: USB_VENDOR_ID, usbProductId: USB_PRODUCT_ID }
                ]
            });
            return this._port;
        } catch (error) {
            if (error.name === 'NotFoundError') {
                throw new Error('No port selected');
            }
            if (error.name === 'SecurityError') {
                throw new Error('Serial port access denied');
            }
            throw error;
        }
    }

    /**
     * Connect to the serial port
     */
    async connect() {
        if (!this._port) {
            await this.requestPort();
        }

        try {
            await this._port.open({
                baudRate: 115200,
                dataBits: 8,
                stopBits: 1,
                parity: 'none',
                flowControl: 'none'
            });

            this._disconnecting = false;
            this._writer = this._port.writable.getWriter();
            this._connected = true;

            // Start reading
            this._startReadLoop();

            // Disable echo first. The device can still send the echoed command
            // before ~EOK, so wait for the exact acknowledgement.
            await this._handshake();

            this.emit('connected', this._deviceInfo);
            return true;
        } catch (error) {
            await this.disconnect();

            // Provide user-friendly error messages
            if (error.name === 'InvalidStateError') {
                throw new Error('Port is already open');
            }
            if (error.name === 'NetworkError') {
                throw new Error('Port is busy (used by another application)');
            }

            this.emit('error', error);
            throw error;
        }
    }

    /**
     * Disconnect from the serial port
     */
    async disconnect() {
        if (this._disconnectPromise) {
            return this._disconnectPromise;
        }

        this._disconnectPromise = this._disconnectInternal();
        try {
            await this._disconnectPromise;
        } finally {
            this._disconnectPromise = null;
        }
    }

    async _disconnectInternal() {
        this._disconnecting = true;
        this._readLoopActive = false;
        // Make already queued commands fail before any asynchronous close work.
        this._connected = false;

        this._rejectPendingResponse(new Error('Disconnected'));

        try {
            if (this._reader) {
                await this._reader.cancel().catch(() => {});
                this._reader.releaseLock();
                this._reader = null;
            }
        } catch (e) {
            // Ignore reader errors
        }

        try {
            if (this._writer) {
                await this._writer.close().catch(() => {});
                this._writer.releaseLock();
                this._writer = null;
            }
        } catch (e) {
            // Ignore writer errors
        }

        try {
            if (this._port) {
                await this._port.close();
            }
        } catch (e) {
            // Ignore close errors (port may already be closed)
        }

        this._port = null;
        this._deviceInfo = '';
        this._readBuffer = '';
        this._disconnecting = false;
        this.emit('disconnected');
    }

    /**
     * Start the read loop
     */
    async _startReadLoop() {
        if (!this._port?.readable) return;

        this._readLoopActive = true;
        this._reader = this._port.readable.getReader();

        try {
            while (this._readLoopActive) {
                const { value, done } = await this._reader.read();
                if (done) break;

                const text = new TextDecoder().decode(value);
                this._readBuffer += text;
                this._processBuffer();
            }
        } catch (error) {
            if (this._readLoopActive) {
                this.emit('error', error);
                await this.disconnect();
            }
        }
    }

    /**
     * Process the read buffer for complete responses
     */
    _processBuffer() {
        // Look for complete lines ending with \r\n or \n
        let newlineIndex;
        while ((newlineIndex = this._readBuffer.indexOf('\n')) !== -1) {
            let line = this._readBuffer.substring(0, newlineIndex).trim();
            this._readBuffer = this._readBuffer.substring(newlineIndex + 1);

            if (line.length === 0) continue;

            // Emit raw data for terminal
            this.emit('data', line);

            const responseLine = this._normalizeResponseLine(line);
            const pending = this._pendingResponse;

            // Handle response if waiting. Stray echo/diagnostic lines are kept
            // in the terminal log but are not allowed to satisfy another command.
            if (pending && pending.matcher(responseLine, line)) {
                this._resolvePendingResponse(responseLine);
            }
        }
    }

    _normalizeResponseLine(line) {
        return line.startsWith('~') ? line.substring(1) : line;
    }

    /**
     * Send raw data
     */
    async _sendRaw(data) {
        if (!this._writer || !this._connected) {
            throw new Error('Not connected');
        }

        try {
            const encoded = new TextEncoder().encode(data);
            await this._writer.write(encoded);
        } catch (error) {
            // Device was disconnected during write
            if (error.name === 'NetworkError' || error.message?.includes('disconnected')) {
                void this.disconnect();
                throw new Error('Device disconnected');
            }
            throw error;
        }
    }

    /**
     * Send a command and wait for response
     */
    async sendCommand(cmd, params = '', timeout = 2000, options = {}) {
        if (!this._connected) {
            throw new Error('Not connected');
        }

        return this._enqueueCommand(() => this._sendCommandLocked(cmd, params, timeout, options));
    }

    async _sendCommandLocked(cmd, params = '', timeout = 2000, options = {}) {
        if (!this._connected || this._disconnecting) {
            throw new Error('Not connected');
        }

        const validatedParams = validateCdcCommand(cmd, params);

        // Format command
        const fullCmd = `~${cmd}${validatedParams}\r\n`;

        // Emit for terminal
        this.emit('command', fullCmd.trim());

        const expectedPrefix = options.expectedPrefix ?? cmd;
        const matcher = options.matcher || ((line) => {
            return expectedPrefix ? line.startsWith(expectedPrefix) : true;
        });
        const responsePromise = this._waitForResponse(matcher, timeout);

        // Send command
        try {
            await this._sendRaw(fullCmd);
            return await responsePromise;
        } catch (error) {
            this._rejectPendingResponse(error);
            await responsePromise.catch(() => {});
            throw error;
        }
    }

    _enqueueCommand(operation) {
        const run = this._commandQueue.then(operation, operation);
        this._commandQueue = run.catch(() => {});
        return run;
    }

    _waitForResponse(matcher, timeout) {
        if (this._pendingResponse) {
            throw new Error('Serial command already pending');
        }

        return new Promise((resolve, reject) => {
            const pending = {
                matcher,
                resolve,
                reject,
                timer: setTimeout(() => {
                    if (this._pendingResponse === pending) {
                        this._pendingResponse = null;
                        const error = new Error('Timeout');
                        error.code = 'SERIAL_TIMEOUT';
                        reject(error);
                        // The protocol has no request identifier. Closing the
                        // port is the only reliable way to prevent a late G
                        // response from satisfying the next G request.
                        void this.disconnect();
                    }
                }, timeout)
            };
            this._pendingResponse = pending;
        });
    }

    _resolvePendingResponse(response) {
        const pending = this._pendingResponse;
        if (!pending) return;

        clearTimeout(pending.timer);
        this._pendingResponse = null;
        pending.resolve(response);
    }

    _rejectPendingResponse(error) {
        const pending = this._pendingResponse;
        if (!pending) return;

        clearTimeout(pending.timer);
        this._pendingResponse = null;
        pending.reject(error);
    }

    async _handshake() {
        const info = await this._enqueueCommand(async () => {
            await this._sendCommandLocked('E', '0', 1000, {
                matcher: (line) => line.startsWith('EOK')
            });

            this._readBuffer = '';

            return this._sendCommandLocked('I', '', 1500);
        });

        this._deviceInfo = info.startsWith('I') ? info.substring(1).trim() : info.trim();
    }

    /**
     * Get all pin modes
     * Returns 10-character string representing modes of pins 1-10
     */
    async getModes() {
        const response = await this.sendCommand('M');
        if (response && response.startsWith('M')) {
            const modes = response.substring(1);
            try {
                return validateModes(modes);
            } catch {
                return null;
            }
        }
        return null;
    }

    /**
     * Set all pin modes
     * @param {string} modes - 10-character string of mode letters
     */
    async setModes(modes) {
        const validatedModes = validateModes(modes);
        const response = await this.sendCommand('D', validatedModes);
        // Device responds with ~D<modes> (10 chars of actual applied modes)
        if (response && response.startsWith('D')) {
            const actualModes = response.substring(1);
            try {
                return validateModes(actualModes);
            } catch {
                return null;
            }
        }
        return null;
    }

    /**
     * Get all pin values
     */
    async getValues() {
        const response = await this.sendCommand('V');
        // Device responds with ~V<values>, remove the V prefix
        if (response && response.startsWith('V')) {
            return response.substring(1);
        }
        return response;
    }

    /**
     * Read a single pin value
     * @param {number} pin - Pin number (1-10)
     */
    async readPin(pin, timeout = 3000) {
        const protocolPin = connectorPinToProtocol(pin);
        const response = await this.sendCommand('G', String(protocolPin), timeout);
        if (response && response.startsWith('G')) {
            return response.substring(1);
        }
        return response;
    }

    /**
     * Set a pin value
     * @param {number} pin - Pin number (1-10)
     * @param {string|number} value - Value to set
     */
    async setPin(pin, value) {
        const protocolPin = connectorPinToProtocol(pin);
        const response = await this.sendCommand('S', `${protocolPin}:${value}`);
        return isSuccessfulResponse(response, 'S');
    }

    /**
     * Initialize the shared PWM timer and then apply channel duty.
     */
    async setPWM(pin, duty, frequency) {
        const protocolPin = connectorPinToProtocol(pin);
        const validatedDuty = validatePwmDuty(duty);
        const validatedFrequency = validatePwmFrequency(frequency);

        const initResponse = await this.sendCommand('P', `${protocolPin}:${validatedFrequency}`);
        if (!isSuccessfulResponse(initResponse, 'P')) {
            return false;
        }

        return this.setPin(pin, validatedDuty);
    }

    /**
     * Save configuration to flash
     */
    async saveToFlash() {
        const response = await this.sendCommand('F');
        return isSuccessfulResponse(response, 'F');
    }

    /**
     * Send WS2812 command
     * @param {string} hexData - Hex string of color data
     */
    async sendWS2812(hexData) {
        const validatedHex = validateWS2812Hex(hexData);
        const response = await this.sendCommand('W', validatedHex);
        return isSuccessfulResponse(response, 'W');
    }

    /**
     * Execute a script
     * @param {string} script - Script to execute
     */
    async executeScript(script, timeout = 30000) {
        const validatedScript = validateScript(script);
        if (!validatedScript) {
            throw new Error('Script must not be empty');
        }

        const chunks = splitScript(validatedScript);
        if (chunks.length === 1) {
            const response = await this.sendCommand('X', chunks[0], timeout);
            return isSuccessfulResponse(response, 'X');
        }

        let response = await this.sendCommand('Q', '');
        if (!isSuccessfulResponse(response, 'Q')) {
            return false;
        }

        for (const chunk of chunks) {
            response = await this.sendCommand('Q', chunk);
            if (!isSuccessfulResponse(response, 'Q')) {
                return false;
            }
        }

        response = await this.sendCommand('X', '', timeout);
        return isSuccessfulResponse(response, 'X');
    }

    /**
     * Get device info
     */
    async getDeviceInfo() {
        const response = await this.sendCommand('I');
        return response;
    }

}

// Singleton instance
export const serial = new SerialConnection();
